import cx from 'classnames';
import { get } from 'lodash';
import { useObserver } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FB, FBFieldName, FBTextEditor } from '../..';
import { translate } from '../../../../common/intl';
import { getInnerHTMLProp } from '../../../../common/utils/helpers';
import useTextEditorStyles from '../../FBTextEditor/FBTextEditor.styles';
import { FBMPIProcedureProps } from '../FBMPIProcedure.types';

type FBMPIProcedureDescriptionProps = Pick<FBMPIProcedureProps,
'disabled' | 'autoFocus' | 'autoScrollTo' | 'onKeyDown' | 'name' | 'stepIndex' | 'isPreview'
> & { overrideHtml?: string };

const FBMPIProcedureDescription: React.FC<FBMPIProcedureDescriptionProps> = ({
  disabled = false,
  autoFocus,
  autoScrollTo,
  onKeyDown,
  name = '_undefined_',
  stepIndex,
  overrideHtml,
}) => {
  const classes = useTextEditorStyles();
  const fieldName = `${name}.description`;
  const { formState, workspaceState } = FB.useStores();
  const isDisabled = useObserver(() => disabled
    || !workspaceState?.getIsInputOwner(fieldName)
    || Boolean(formState?.locked.get(fieldName)?.locked));
  const [isEditing, setIsEditing] = useState(Boolean(autoFocus && !isDisabled));

  const formattedValue = useObserver(() => {
    const currentValue: unknown = get(formState?.values, fieldName);
    const fieldValue: unknown = currentValue === undefined
      ? get(formState?.initialValues, fieldName)
      : currentValue;
    if (overrideHtml !== undefined) { return overrideHtml; }
    return typeof fieldValue === 'string' ? fieldValue.replace(/<[^/>]*>([\s]?)*<\/[^>]*>/g, '<p><br></p>') : '';
  });

  useEffect(() => {
    if (isDisabled) { setIsEditing(false); }
  }, [isDisabled]);

  useEffect(() => {
    if (!autoScrollTo) { return; }
    document.getElementById(`${FBFieldName.MPIProcedureId}-${fieldName}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [autoScrollTo, fieldName]);

  if (isDisabled || !isEditing) {
    return (
      <div
        id={`${FBFieldName.MPIProcedureId}-${fieldName}`}
        data-cy={fieldName}
        className={cx(classes.root, { [classes.editablePreview]: !isDisabled })}
        role={isDisabled ? undefined : 'button'}
        tabIndex={isDisabled ? undefined : 0}
        aria-label={isDisabled ? undefined : translate('form.builder.step.number', { number: stepIndex })}
        onClick={isDisabled ? undefined : () => setIsEditing(true)}
        onKeyDown={isDisabled ? undefined : (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsEditing(true);
          }
        }}
      >
        <div
          data-cy={`${fieldName}-mask`}
          className={cx(classes.marginBStyle, classes.previewContent)}
          dangerouslySetInnerHTML={getInnerHTMLProp(formattedValue)}
        />
      </div>
    );
  }

  return (
    <FBTextEditor
      {...{
        disabled,
        onKeyDown,
      }}
      autoFocus
      onBlur={() => setIsEditing(false)}
      name={fieldName}
      gutter={false}
      autosave={false}
      shouldNotAutosaveOnBlur={true}
      boxProps={{ className: classes.root }}
      withState
    />
  );
};

export default FBMPIProcedureDescription;
